import React, { useEffect, useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import axios from "../../api/axios.js";
import "./CopyMechanismDialog.css";

const MARTS_RE = /^marts_[ERM][0-9]{5}$/;

/* MARTS IDs are commonly written without the prefix (e.g. M00315), so accept both. */
function normalizeMarts(value) {
  const trimmed = (value || "").trim();
  if (!trimmed) return "";
  if (/^[ERM][0-9]{5}$/i.test(trimmed)) return "marts_" + trimmed.toUpperCase();
  return trimmed;
}

function moleculeLabel(molecule) {
  if (!molecule) return "?";
  const name = molecule.name || "unnamed molecule";
  return molecule.marts ? `${name} (${molecule.marts})` : name;
}

/* One step in the shape the mechanism form keeps in state. */
function toFormStep(molecule, mechanismType, evidence = "") {
  return {
    molecule: {
      smiles: molecule.smiles || "",
      name: molecule.name || "",
      chebi: molecule.chebi || "",
      marts: molecule.marts || "",
      stereochemistry: molecule.stereochemistry || "uncertain",
      molecule_id: molecule.molecule_id ?? null,
    },
    mechanism: mechanismType || "",
    evidence: evidence || "",
    possible_next: [],
  };
}

/* Same molecule? MARTS IDs win; molecules new to MARTS fall back to SMILES. */
function sameMolecule(a, b) {
  if (!a || !b) return false;
  if (a.marts && b.marts) return a.marts === b.marts;
  if (!a.smiles || !b.smiles) return false;
  return a.smiles === b.smiles && (a.stereochemistry || "uncertain") === (b.stereochemistry || "uncertain");
}

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/*
 * "Copy mechanism" dialog for the mechanism builder on the submit page.
 *
 * Two sources, both of which fill the first n steps starting from the substrate
 * of the mechanism being defined:
 *   - "From MARTS": the prefix of an existing, enzyme-linked mechanism, up to a
 *     chosen intermediate (backend request types mechanismCopyTargets /
 *     mechanismCopyPath).
 *   - "From this submission": the steps already defined for another reaction in
 *     the current form, which must start from the same substrate.
 */
export default function CopyMechanismDialog({ substrate, sessionSources = [], onApply, onClose }) {
  const [mode, setMode] = useState("database");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  // "From MARTS" state
  const [targets, setTargets] = useState([]);
  const [target, setTarget] = useState(null);
  const [options, setOptions] = useState([]);

  // "From this submission" state
  const [sourceOption, setSourceOption] = useState(null);
  const [upToOption, setUpToOption] = useState(null);

  const startMarts = substrate?.marts || "";
  const startIsKnown = MARTS_RE.test(startMarts);

  // Reactions whose in-session mechanism actually starts from this substrate.
  const usableSources = sessionSources.filter((source) => sameMolecule(source.substrate, substrate));
  const skippedSources = sessionSources.length - usableSources.length;

  // Everything reachable downstream of the substrate, for the "up to" picker.
  useEffect(() => {
    let cancelled = false;
    if (mode !== "database" || !startIsKnown) return undefined;
    setBusy(true);
    axios
      .post("/submitcheck", { requestType: "mechanismCopyTargets", marts: startMarts })
      .then((response) => {
        if (cancelled) return;
        const data = response.data;
        if (typeof data === "object" && data !== null && Array.isArray(data.targets)) {
          setTargets(data.targets);
        } else {
          setTargets([]);
          setMessage(`No mechanism in MARTS starts from ${startMarts}.`);
        }
      })
      .catch(() => {
        if (!cancelled) setMessage("Could not reach the database. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, startMarts, startIsKnown]);

  const targetOptions = targets.map((molecule) => ({
    value: molecule.marts,
    label: `${moleculeLabel(molecule)} — ${plural(molecule.steps, "step")}, in ${plural(molecule.mechanisms, "mechanism")}`,
  }));

  const applySteps = (steps, extras) => {
    onApply(steps, extras || {});
    onClose();
  };

  async function findPaths() {
    const targetMarts = normalizeMarts(target?.value);
    setOptions([]);
    if (!MARTS_RE.test(targetMarts)) {
      setMessage("Pick an intermediate from the list, or type its MARTS ID (e.g. marts_M00315).");
      return;
    }
    if (targetMarts === startMarts) {
      setMessage("The intermediate to stop at has to be a molecule after the substrate.");
      return;
    }
    setMessage("");
    setBusy(true);
    try {
      const response = await axios.post("/submitcheck", {
        requestType: "mechanismCopyPath",
        marts: startMarts,
        target: targetMarts,
      });
      const data = response.data;
      if (typeof data === "object" && data !== null && Array.isArray(data.options) && data.options.length > 0) {
        // One path is the normal case — copy it straight away.
        if (data.options.length === 1) {
          applySteps(data.options[0].steps.map((step) => toFormStep(step.molecule, step.mechanism_type)));
          return;
        }
        setOptions(data.options);
      } else {
        setMessage(
          `No mechanism in MARTS goes from ${moleculeLabel(substrate)} to ${targetMarts}. Pick a different intermediate.`
        );
      }
    } catch {
      setMessage("Could not reach the database. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  const selectedSource = usableSources.find((source) => source.index === sourceOption?.value) || null;
  const sourceSteps = selectedSource?.steps || [];
  const upToOptions = [
    { value: -1, label: `All ${plural(sourceSteps.length, "step")}` },
    ...sourceSteps.map((step, i) => ({
      value: i,
      label: `Up to ${moleculeLabel(step.molecule)} — ${plural(i + 1, "step")}`,
    })),
  ];

  function copyFromSession() {
    if (!selectedSource) {
      setMessage("Pick the reaction to copy the mechanism from.");
      return;
    }
    const cut = upToOption && upToOption.value >= 0 ? upToOption.value + 1 : sourceSteps.length;
    const steps = sourceSteps
      .slice(0, cut)
      .map((step) => toFormStep(step.molecule || {}, step.mechanism, step.evidence));
    if (steps.length === 0) {
      setMessage("That reaction has no mechanism steps to copy yet.");
      return;
    }
    applySteps(steps, {
      evidence: selectedSource.evidence,
      publication: selectedSource.publication,
      doi: selectedSource.doi,
    });
  }

  return (
    <div className="copy-mech-backdrop" onClick={onClose}>
      <div className="copy-mech-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="copy-mech-header">
          <h3>Copy mechanism</h3>
          <button className="copy-mech-close" onClick={onClose} title="Close">
            ×
          </button>
        </div>
        <div className="copy-mech-body">
          <div className="copy-mech-tabs">
            <button
              className={`copy-mech-tab${mode === "database" ? " active" : ""}`}
              onClick={() => {
                setMode("database");
                setMessage("");
              }}
            >
              From MARTS
            </button>
            <button
              className={`copy-mech-tab${mode === "session" ? " active" : ""}`}
              onClick={() => {
                setMode("session");
                setMessage("");
                setOptions([]);
              }}
            >
              From this submission
            </button>
          </div>

          <div className="copy-mech-row">
            <span className="copy-mech-label">Starting from</span>
            <span className="copy-mech-mol copy-mech-start">{moleculeLabel(substrate)}</span>
          </div>
          <p className="copy-mech-hint">
            The copied steps always start from the substrate of this mechanism and replace the steps defined here so far.
          </p>

          {mode === "database" ? (
            !startIsKnown ? (
              <div className="copy-mech-message">
                The substrate has to exist in MARTS before a mechanism can be copied from the database. Fill in its
                SMILES or MARTS ID first, or copy from another reaction in this submission.
              </div>
            ) : (
              <>
                <div className="copy-mech-row">
                  <span className="copy-mech-label">Copy up to</span>
                  <div className="copy-mech-select">
                    <CreatableSelect
                      isClearable
                      options={targetOptions}
                      value={target}
                      onChange={(choice) => setTarget(choice)}
                      placeholder="Intermediate to stop at, or a MARTS ID..."
                      formatCreateLabel={(input) => `Use ${normalizeMarts(input)}`}
                      isLoading={busy && targets.length === 0}
                      menuPortalTarget={typeof document === "undefined" ? null : document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 1100 }) }}
                    />
                  </div>
                  <button className="basic-button" onClick={findPaths} disabled={busy}>
                    Find mechanism
                  </button>
                </div>
                <p className="copy-mech-hint">
                  Only mechanisms that are already used by an enzyme in MARTS are offered. The step ending in the chosen
                  intermediate is the last one copied — the arrow to your product stays for you to fill in.
                </p>
              </>
            )
          ) : (
            <>
              <div className="copy-mech-row">
                <span className="copy-mech-label">Copy from reaction</span>
                <div className="copy-mech-select">
                  <Select
                    isClearable
                    options={usableSources.map((source) => ({
                      value: source.index,
                      label: `${source.label} — ${plural(source.steps.length, "step")}`,
                    }))}
                    value={sourceOption}
                    onChange={(choice) => {
                      setSourceOption(choice);
                      setUpToOption(null);
                      setMessage("");
                    }}
                    placeholder={
                      usableSources.length > 0 ? "Reaction defined in this submission..." : "No mechanism to copy yet"
                    }
                    isDisabled={usableSources.length === 0}
                    menuPortalTarget={typeof document === "undefined" ? null : document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 1100 }) }}
                  />
                </div>
              </div>
              <div className="copy-mech-row">
                <span className="copy-mech-label">Copy up to</span>
                <div className="copy-mech-select">
                  <Select
                    isClearable
                    options={upToOptions}
                    value={upToOption}
                    onChange={(choice) => setUpToOption(choice)}
                    placeholder="All steps"
                    isDisabled={!selectedSource}
                    menuPortalTarget={typeof document === "undefined" ? null : document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 1100 }) }}
                  />
                </div>
                <button className="basic-button" onClick={copyFromSession} disabled={!selectedSource}>
                  Copy steps
                </button>
              </div>
              {usableSources.length === 0 ? (
                <div className="copy-mech-message">
                  No other reaction in this submission has a mechanism starting from {moleculeLabel(substrate)}
                  {skippedSources > 0
                    ? ` (${plural(skippedSources, "mechanism")} defined here start from a different substrate)`
                    : ""}
                  . Define one first, then come back to copy it.
                </div>
              ) : (
                <p className="copy-mech-hint">
                  The mechanism evidence and publication of the chosen reaction are copied along with the steps.
                  {skippedSources > 0
                    ? ` ${plural(skippedSources, "other mechanism")} defined here start from a different substrate and cannot be copied.`
                    : ""}
                </p>
              )}
            </>
          )}

          {message && <div className="copy-mech-message">{message}</div>}

          {options.length > 0 && (
            <>
              <p className="copy-mech-hint">
                {plural(options.length, "different route")} lead to that intermediate — pick the one you mean.
              </p>
              {options.map((option, index) => (
                <div className="copy-mech-option" key={index}>
                  <div className="copy-mech-option-head">
                    {plural(option.steps.length, "step")} · {plural(option.mechanisms.length, "mechanism")} ·{" "}
                    {plural(option.enzyme_count, "enzyme")}
                    {option.products.length > 0
                      ? ` · leads to ${option.products.slice(0, 3).join(", ")}${
                          option.products.length > 3 ? ", ..." : ""
                        }`
                      : ""}
                  </div>
                  <div className="copy-mech-chain">
                    <span className="copy-mech-mol copy-mech-start">{substrate?.name || startMarts}</span>
                    {option.steps.map((step, stepIndex) => (
                      <React.Fragment key={stepIndex}>
                        <span className="copy-mech-arrow">&rarr; {step.mechanism_type} &rarr;</span>
                        <span className="copy-mech-mol">{step.molecule.name || step.molecule.marts}</span>
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="copy-mech-sources">
                    {option.mechanisms.slice(0, 6).join(", ")}
                    {option.mechanisms.length > 6 ? `, +${option.mechanisms.length - 6} more` : ""}
                  </div>
                  <button
                    className="basic-button"
                    onClick={() =>
                      applySteps(option.steps.map((step) => toFormStep(step.molecule, step.mechanism_type)))
                    }
                  >
                    Use this route
                  </button>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
